import { useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import type { PolicePresenceRequested } from "@thalamos/common";

import {
  Banner,
  BannerList,
  Button,
  PopupDialog,
  PopupDialogTitle,
  TextField,
} from "~/components/design-system/index.js";

export type RequestPolicePresenceDialogProps = {
  onClose: () => void;
  onSubmit: (data: PolicePresenceRequested["recordPolicePresenceData"]) => void;
  isDisabled: boolean;
  onGoBack: () => void;
};

export function RecordPolicePresenceDialog({
  onClose,
  onSubmit,
  isDisabled,
  onGoBack,
}: RequestPolicePresenceDialogProps) {
  const { t } = useTranslation();

  const [recordPresence, setRecordPresence] = useState<"yes" | "no" | null>(
    null,
  );

  const [officerName, setOfficerName] = useState<string>("");
  const [reasoning, setReasoning] = useState<string>("");

  const [errors, setErrors] = useState<{
    officerName?: string;
    reasoning?: string;
  }>({});

  const handleSubmit = () => {
    const trimmedOfficerName = officerName.trim();
    const trimmedReasoning = reasoning.trim();

    if (recordPresence === "yes" && !trimmedOfficerName) {
      errors.officerName = t(
        "pages.policeIncidentFormPage.ongoingPolicePresenceDialog.recordPolicePresence.errors.officer",
      );
    }

    if (errors.officerName) {
      setErrors({ ...errors });
      return;
    }

    onSubmit({
      hasRemainedAtPlaceOfSafety: {
        valueLabel: recordPresence === "yes" ? "Yes" : "No",
        value: recordPresence!,
      },

      officerName: trimmedOfficerName
        ? {
            value: trimmedOfficerName,
          }
        : null,

      reasoning: trimmedReasoning
        ? {
            value: trimmedReasoning,
          }
        : null,
    });
  };

  return (
    <PopupDialog open={true} onClose={onClose}>
      <PopupDialogTitle
        titleText={"Inform Police Presence"}
        subtitleText={"Step 3 of 3"}
        closeDialog={() => {
          onClose();
        }}
      />
      <Box
        sx={{ display: "flex", p: 2, flexDirection: "column", gap: "1.5rem" }}
      >
        <FormControl>
          <FormLabel id="demo-radio-buttons-group-label">
            Have the police remained following your request?
          </FormLabel>
          <RadioGroup
            aria-labelledby="demo-radio-buttons-group-label"
            name="radio-buttons-group"
            value={recordPresence}
            onChange={(e) => {
              setRecordPresence(e.target.value as "yes" | "no");
              if (e.target.value === "no") {
                setErrors((prev) => ({
                  ...prev,
                  officerName: undefined,
                }));
              }
            }}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>

        <Banner
          bannerType={BannerList.WARNING}
          title={`You must communicate with officers outside eMHA by Thalamos to confirm actions they will take. If officers are absent, make sure they have permanently left before clicking 'No'.`}
        />

        <FormControl data-testid="field:officerName">
          <TextField
            name="officerName"
            useFullWidth
            label={t(
              "pages.policeIncidentFormPage.ongoingPolicePresenceDialog.recordPolicePresence.officerLabel",
            )}
            value={officerName}
            onChange={(e) => {
              setOfficerName(e);
              setErrors((prev) => ({ ...prev, officerName: undefined }));
            }}
            errorMessage={errors.officerName}
            showHelperText={errors.officerName !== undefined}
            placeholder={t(
              "pages.policeIncidentFormPage.ongoingPolicePresenceDialog.recordPolicePresence.officerPlaceholder",
            )}
          />
        </FormControl>

        <FormControl>
          <TextField
            name="furtherReasoning"
            useFullWidth
            multiline
            rows="4"
            label={t(
              "pages.policeIncidentFormPage.ongoingPolicePresenceDialog.recordPolicePresence.reasonLabel",
            )}
            value={reasoning}
            errorMessage={errors.reasoning}
            showHelperText={errors.reasoning !== undefined}
            onChange={(e) => {
              setReasoning(e);
            }}
          />
        </FormControl>

        <Box
          sx={{
            marginTop: "2em",
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Button
            label={t("buttonLabels.goBack")}
            testId="close-button"
            onClick={() => {
              onGoBack();
            }}
            variant="outlined"
          />
          <Button
            label={t("buttonLabels.confirm")}
            testId="confirm-button"
            type="submit"
            disabled={isDisabled || recordPresence === null}
            onClick={() => {
              handleSubmit();
            }}
          />
        </Box>
      </Box>
    </PopupDialog>
  );
}
